import React, { useState, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { useConversation } from '../context/ConversationContext';
import { useAPI } from '../context/APIContext';
import { useMsal } from "@azure/msal-react";
 
// Create an interface for the ref
interface ChatInputRef {
  submitMessage: (message: string) => Promise<void>;
}
  
const ChatInput: React.FC = () => {
  const [message, setMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isImageGeneration, setIsImageGeneration] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [documentNames, setDocumentNames] = useState<string[]>([]); // eslint-disable-line @typescript-eslint/no-unused-vars
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const {
    sendMessage,
    loadingMessage,
    selectedModel,
    setLoadingMessage,
    currentConversation,
    setCurrentConversation,
    setConversations,
    setActiveConversationId,
    fetchChatHistory,
    selectedMode,
    streamingText,
    isStreaming,
    setStreamingText,
    setIsStreaming
  } = useConversation();
  const api = useAPI();
  const { accounts } = useMsal();
 
  // Check if Bing search is available
  const isBingSearchAvailable = selectedModel === "gpt-4o";
  
  // Disable file upload and image generation in internal mode
  const isInternalMode = selectedMode === 'internal';

  // Auto-enable search when in external mode with gpt-4o
  React.useEffect(() => {
    if (selectedMode === 'external' && selectedModel === 'gpt-4o') {
      setIsSearchMode(true);
    } else {
      setIsSearchMode(false);
    }
  }, [selectedMode, selectedModel]);

  // Standardized title sanitization utility - used everywhere for consistency
  const sanitizeTitle = (title: string, maxLength: number = 50, fallback: string = 'New Chat'): string => {
    if (!title || typeof title !== 'string' || title.trim().length === 0) {
      return fallback;
    }
    
    const cleanTitle = title.trim();
    
    // If title is exactly the fallback or common defaults, generate from content
    if (cleanTitle === fallback || cleanTitle === 'Chat Session' || cleanTitle === 'New Chat') {
      return fallback;
    }
    
    // Limit length and add ellipsis if needed
    if (cleanTitle.length > maxLength) {
      return cleanTitle.substring(0, maxLength) + '...';
    }
    
    return cleanTitle;
  };

  // Export sanitizeTitle globally for use in other components
  React.useEffect(() => {
    (window as any).sanitizeTitle = sanitizeTitle;
    return () => {
      delete (window as any).sanitizeTitle;
    };
  }, []);

  // Function to generate a better conversation title (for external mode)
  const generateConversationTitle = (message: string): string => {
    if (!message || message.trim().length === 0) {
      return 'New Chat';
    }
    
    // Take first 50 characters and add ellipsis if longer
    const title = message.trim();
    if (title.length > 50) {
      return title.substring(0, 50) + '...';
    }
    return title;
  };

  // Separate function for internal mode title generation
  const generateInternalModeTitle = (message: string): string => {
    if (!message || message.trim().length === 0) {
      return 'Chat Session';
    }
    
    // Take first 50 characters and add ellipsis if longer for internal mode
    const title = message.trim();
    if (title.length > 50) {
      return title.substring(0, 50) + '...';
    }
    return title;
  };
 
  // The updated handleSubmit function with conversation_id storage fix
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // In internal mode, only allow text messages
    if (isInternalMode && (selectedFiles.length > 0 || isImageGeneration)) {
      alert('File uploads and image generation are not supported in internal mode. Please switch to external mode.');
      return;
    }
    
    if ((message.trim() || selectedFiles.length > 0) && !loadingMessage && !isStreaming && selectedModel) {
      try {
        setLoadingMessage(true);
        const currentMessage = message.trim();
        const currentFiles = [...selectedFiles];
 
        // Set document names immediately from selected files
        const initialDocNames = currentFiles.map(file => file.name);
        setDocumentNames(initialDocNames);
 
        // Get conversation_id from context or sessionStorage
        const conversationIdToSend = currentConversation?.id || sessionStorage.getItem('conversation_id') || '';
        const userIdToSend = sessionStorage.getItem('user_id') || '';
 
        // Clear input immediately
        setMessage('');
        setSelectedFiles([]);
        setIsImageGeneration(false);
        setIsSearchMode(false);
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
        }
 
        // Add user message to the conversation first with initial document names
        await sendMessage(currentMessage, undefined, userIdToSend, undefined, initialDocNames);
 
        // Define a proper type for the response
        interface ApiResponse {
          conversation_id?: string;
          response?: string;
          response_type?: string;
          image_url?: string;
          document_names?: string[];
          sources?: { [key: string]: { title: string; url: string; snippet?: string } };
          ai_response?: string;
          user_message?: string;
          success?: boolean;
          thread_id?: string; // Added for internal mode
          session_id?: string; // Added for internal mode
          source_documents?: any[];
          keywords?: any[];
          document_count?: number;
        }
 
        let response: ApiResponse = {};
 
        if (selectedMode === 'internal') {
          // For internal mode, use existing session ID or let backend handle new session creation
          let conversationIdToSend = currentConversation?.id || sessionStorage.getItem('conversation_id') || '';

          // For internal mode, always use streaming API
          try {
            const streamResponse = await api.v2ChatStream(currentMessage, conversationIdToSend);
            
            // Handle streaming response
            if (streamResponse && streamResponse.body) {
              const reader = streamResponse.body.getReader();
              const decoder = new TextDecoder();
              let aiResponse = '';
              let metadata: any = {};
              let buffer = '';
              
              // Get the current session ID
              const currentSessionId = conversationIdToSend || currentConversation?.id || sessionStorage.getItem('conversation_id') || '';

              // Update conversation state
              setCurrentConversation(prev => {
                const updated = prev
                  ? { ...prev, id: currentSessionId || prev.id }
                  : {
                      id: currentSessionId || '',
                      title: currentMessage || 'New Chat',
                      created_at: new Date().toISOString(),
                      model: selectedModel,
                      messages: []
                    };
                // Update the conversations list
                setConversations(convs => {
                  const filtered = convs.filter(c => c.id && c.id !== '');
                  if (!filtered.find(c => c.id === currentSessionId)) {
                    return [updated, ...filtered];
                  }
                  return filtered.map(c => c.id === '' ? updated : c);
                });
                return updated;
              });

              // Don't send user message separately - will send both together at the end

              try {
                // 🎯 START STREAMING MODE - Enable streaming states
                setIsStreaming(true);
                setStreamingText("");
                setLoadingMessage(false);
                
                while (true) {
                  const { done, value } = await reader.read();
                  
                  if (done) {
                    break;
                  }
                  
                  const chunk = decoder.decode(value);
                  buffer += chunk;
                  
                  // Process complete lines
                  const lines = buffer.split('\n');
                  buffer = lines.pop() || '';
                  
                  for (const line of lines) {
                    if (line.trim() === '') continue;
                    
                    if (line.startsWith('data: ')) {
                      try {
                        const data = JSON.parse(line.slice(6));
                        
                        switch (data.type) {
                          case 'metadata':
                            metadata = data;
                            if (data.session_id) {
                              const newSessionId = data.session_id.toString();
                              sessionStorage.setItem('conversation_id', newSessionId);
                              sessionStorage.setItem('session_id', newSessionId);
                              setActiveConversationId(newSessionId);
                            }
                            if (data.thread_id) {
                              sessionStorage.setItem('thread_id', data.thread_id);
                            }
                            break;
                          case 'content':
                            aiResponse = data.content;
                            // 🎯 UPDATE STREAMING TEXT - Show progressive content
                            setStreamingText(aiResponse);
                            break;
                          case 'complete':
                            aiResponse = data.content;
                            // 🎯 COMPLETE STREAMING - Disable streaming mode
                            setStreamingText(aiResponse);
                            setIsStreaming(false);
                            // Send both user message and AI response together (like original)
                            await sendMessage(currentMessage, aiResponse, userIdToSend);
                            break;
                        }
                      } catch (parseError) {
                        console.error('Error parsing stream data:', parseError);
                      }
                    }
                  }
                }
              } catch (streamError) {
                console.error('Error reading stream:', streamError);
                setLoadingMessage(false);
                // 🎯 RESET STREAMING STATE on error
                setIsStreaming(false);
                setStreamingText("");
              }
              
              // After streaming completes, refresh chat history for internal mode
              if (userIdToSend) {
                fetchChatHistory(userIdToSend, 1, 30);
              }
              
              // Set response for validation - streaming already handled sending messages
              response = { ai_response: aiResponse };
            } else {
              // Fallback for non-streaming response
              response = streamResponse;
              if (streamResponse?.session_id) {
                sessionStorage.setItem('conversation_id', streamResponse.session_id);
              }
              if (streamResponse?.thread_id) {
                sessionStorage.setItem('thread_id', streamResponse.thread_id);
              }
            }
          } catch (error) {
            console.error('Error in streaming API call:', error);
            // Fallback to regular API
            response = await api.v2Chat(currentMessage, conversationIdToSend);
          }
        } else {
          // For external mode, use the appropriate API based on features
          if (isSearchMode && isBingSearchAvailable) {
            try {
            response = await api.bingGroundingSearch(currentMessage, selectedModel, conversationIdToSend, userIdToSend);
            } catch (error) {
              console.error('Bing Grounding Search failed, falling back to regular chat:', error);
              // Keep search enabled - only disable on mode change or manual toggle
              response = await api.generateResponse(selectedModel, currentMessage, userIdToSend, { conversation_id: conversationIdToSend });
            }
          } else if (isImageGeneration) {
            response = await api.generateResponse(selectedModel, currentMessage, userIdToSend, { generate_image: true, conversation_id: conversationIdToSend });
          } else if (selectedFiles.length > 0) {
            response = await api.generateResponse(selectedModel, currentMessage, userIdToSend, { documents: currentFiles, conversation_id: conversationIdToSend });
          } else {
            response = await api.generateResponse(selectedModel, currentMessage, userIdToSend, { conversation_id: conversationIdToSend });
          }
        }

        if (response && (response.response || response.ai_response || response.image_url)) {
          // Extract the AI response based on the mode
          const aiResponse = selectedMode === 'internal' 
            ? response.ai_response || response.response 
            : response.response;

          if (response.conversation_id && selectedMode === 'external') {
            sessionStorage.setItem('conversation_id', response.conversation_id);
          }

          // Handle title generation separately for each mode
          let newTitle = '';
          let shouldGenerateTitle = false;

          if (selectedMode === 'external' && (!currentConversation || !currentConversation.title || currentConversation.title === 'New Chat')) {
            newTitle = generateConversationTitle(currentMessage);
            shouldGenerateTitle = true;
          } else if (selectedMode === 'internal' && (!currentConversation || !currentConversation.title || currentConversation.title === 'Untitled Chat' || (currentConversation && currentConversation.messages && currentConversation.messages.length === 0))) {
            // Generate title for internal mode when no conversation, no title, title is default, or first message
            newTitle = generateInternalModeTitle(currentMessage);
            shouldGenerateTitle = true;
          }

          if (shouldGenerateTitle) {
            
            // Store or update conversation with generated title
            if (response.conversation_id || conversationIdToSend) {
              const conversationId = response.conversation_id || conversationIdToSend;
              
              // Update current conversation with title
              if (currentConversation) {
                const updatedConversation = {
                  ...currentConversation,
                  title: newTitle,
                  id: conversationId
                };
                setCurrentConversation(updatedConversation);
                
                // Update conversations list
                setConversations(prev => {
                  const existingIndex = prev.findIndex(conv => conv.id === conversationId);
                  if (existingIndex >= 0) {
                    const updated = [...prev];
                    updated[existingIndex] = { ...updated[existingIndex], title: newTitle };
                    return updated;
                  } else {
                    return [updatedConversation, ...prev];
                  }
                });
              }
            }
          }

          // Add both user message and AI response to the conversation
            await sendMessage(
            currentMessage, // Send the user message
            aiResponse,
              userIdToSend,
            response.image_url,
            response.document_names || [],
              response.sources
            );

          if (response.document_names) {
            setDocumentNames(response.document_names);
          } else {
            setDocumentNames([]);
          }

          // Set active conversation ID
          if (response.conversation_id) {
            setActiveConversationId(response.conversation_id);
          }

          // Refresh chat history if user ID is available
          if (userIdToSend) {
            fetchChatHistory(userIdToSend, 1, 30);
          }
        } else {
          console.error('No valid response received from API');
        }
      } catch (error: any) {
        console.error('Error sending message:', error);
      } finally {
        setLoadingMessage(false);
        // 🎯 ENSURE STREAMING STATE IS RESET
        setIsStreaming(false);
      }
    }
  };

  // Remove unused function to clean up warnings
  // const handleStreamingResponse = async (currentMessage: string, mode: 'internal' | 'external') => {
  //   // Function removed to avoid unused variable warning
  // };

  const submitMessageDirectly = async (messageText: string) => {
    if (!messageText.trim() || loadingMessage || isStreaming || !selectedModel) {
      return;
    }

    // Directly execute the submit logic with the provided message (bypass state)
    try {
      setLoadingMessage(true);
      const currentMessage = messageText.trim();

      // Get conversation_id from context or sessionStorage
      const conversationIdToSend = currentConversation?.id || sessionStorage.getItem('conversation_id') || '';
      const userIdToSend = sessionStorage.getItem('user_id') || '';

      // Clear input immediately
      setMessage('');
      setSelectedFiles([]);
      setIsImageGeneration(false);
      setIsSearchMode(false);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }

      // 🎯 ADD USER MESSAGE TO UI IMMEDIATELY
      // Create immediate user message display without backend call
      if (currentConversation) {
        const userMessage = {
          role: 'user' as const,
          content: currentMessage,
          timestamp: new Date().toISOString(),
        };
        
        setCurrentConversation(prev => prev ? {
          ...prev,
          messages: [...prev.messages, userMessage]
        } : null);
      } else {
        // Create new conversation with user message
        const newConversation = {
          id: '', // Will be set from backend response
          title: currentMessage.slice(0, 50) + (currentMessage.length > 50 ? '...' : ''),
          created_at: new Date().toISOString(),
          model: selectedModel,
          messages: [{
            role: 'user' as const,
            content: currentMessage,
            timestamp: new Date().toISOString(),
          }]
        };
        setCurrentConversation(newConversation);
      }

      let response: any = {};

      if (selectedMode === 'internal') {
        // Internal mode logic (streaming)
        
        try {
          const streamResponse = await api.v2ChatStream(currentMessage, conversationIdToSend);
          
          if (streamResponse && streamResponse.body) {
            const reader = streamResponse.body.getReader();
            let aiResponse = '';
            
            try {
              // Start streaming mode
              setIsStreaming(true);
              setStreamingText("");
              setLoadingMessage(false);
              
              while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                
                const chunk = new TextDecoder().decode(value);
                
                const lines = chunk.split('\n');
                for (const line of lines) {
                  if (line.startsWith('data: ') && line !== 'data: [DONE]') {
                    try {
                      const jsonStr = line.slice(6);
                      const data = JSON.parse(jsonStr);
                      
                      switch (data.type) {
                        case 'content':
                          const newContent = data.content;
                          // Only update if content actually changed
                          if (newContent !== aiResponse) {
                            aiResponse = newContent;
                            // Update streaming text for progressive display
                            setStreamingText(newContent);
                          }
                          break;
                        case 'complete':
                          aiResponse = data.content;
                          setStreamingText(aiResponse);
                          setIsStreaming(false);
                          
                          // 🎯 COMPLETE STREAMING - Add final AI response
                          setCurrentConversation(prev => {
                            if (!prev) return null;
                            const assistantMessage = {
                              role: 'assistant' as const,
                              content: aiResponse,
                              timestamp: new Date().toISOString(),
                            };
                            return {
                              ...prev,
                              messages: [...prev.messages, assistantMessage]
                            };
                          });
                          
                          // Backend persistence (duplicate user message will be prevented)
                          await sendMessage(currentMessage, aiResponse, userIdToSend);
                          setStreamingText(""); // Clear streaming text after message is sent
                          break;
                      }
                    } catch (parseError) {
                      console.error('Error parsing stream data:', parseError);
                    }
                  }
                }
              }
            } catch (streamError) {
              console.error('Error reading stream:', streamError);
              setLoadingMessage(false);
              setIsStreaming(false);
              setStreamingText("");
            }
            
            // After streaming completes, refresh chat history for internal mode
            if (userIdToSend) {
              fetchChatHistory(userIdToSend, 1, 30);
            }
            
            response = { ai_response: aiResponse };
          } else {
            response = streamResponse;
            if (streamResponse?.session_id) {
              sessionStorage.setItem('conversation_id', streamResponse.session_id);
            }
            if (streamResponse?.thread_id) {
              sessionStorage.setItem('thread_id', streamResponse.thread_id);
            }
          }
        } catch (error) {
          console.error('Error in streaming API call:', error);
          setIsStreaming(false);
          setStreamingText("");
          response = await api.v2Chat(currentMessage, conversationIdToSend);
        }
      } else {
        // External mode logic
        if (isSearchMode && isBingSearchAvailable) {
          try {
            response = await api.bingGroundingSearch(currentMessage, selectedModel, conversationIdToSend, userIdToSend);
          } catch (error) {
            console.error('Bing Grounding Search failed, falling back to regular chat:', error);
            response = await api.generateResponse(selectedModel, currentMessage, userIdToSend, { conversation_id: conversationIdToSend });
          }
        } else {
          response = await api.generateResponse(selectedModel, currentMessage, userIdToSend, { conversation_id: conversationIdToSend });
        }
      }

      if (response && (response.response || response.ai_response || response.image_url)) {
        const aiResponse = selectedMode === 'internal' 
          ? response.ai_response || response.response 
          : response.response;

        if (response.conversation_id && selectedMode === 'external') {
          sessionStorage.setItem('conversation_id', response.conversation_id);
        }

        // Handle title generation separately for each mode
        let newTitle = '';
        let shouldGenerateTitle = false;

        if (selectedMode === 'external' && (!currentConversation || !currentConversation.title || currentConversation.title === 'New Chat')) {
          newTitle = generateConversationTitle(currentMessage);
          shouldGenerateTitle = true;
        } else if (selectedMode === 'internal' && (!currentConversation || !currentConversation.title || currentConversation.title === 'Untitled Chat' || (currentConversation && currentConversation.messages && currentConversation.messages.length === 0))) {
          // Generate title for internal mode when no conversation, no title, title is default, or first message
          newTitle = generateInternalModeTitle(currentMessage);
          shouldGenerateTitle = true;
        }

        if (shouldGenerateTitle) {
          if (response.conversation_id || conversationIdToSend) {
            const conversationId = response.conversation_id || conversationIdToSend;
            
            if (currentConversation) {
              const updatedConversation = {
                ...currentConversation,
                title: newTitle,
                id: conversationId
              };
              setCurrentConversation(updatedConversation);
              
              setConversations(prev => {
                const existingIndex = prev.findIndex(conv => conv.id === conversationId);
                if (existingIndex >= 0) {
                  const updated = [...prev];
                  updated[existingIndex] = { ...updated[existingIndex], title: newTitle };
                  return updated;
            } else {
                  return [updatedConversation, ...prev];
                }
              });
            }
          }
        }
 
        // Add both user message and AI response to the conversation (skip for internal mode as it's handled in streaming)
        if (selectedMode !== 'internal') {
          await sendMessage(
            currentMessage,
            aiResponse,
            userIdToSend,
            response.image_url,
            response.document_names || [],
            response.sources
          );
        }

        // Refresh chat history
        if (userIdToSend) {
          fetchChatHistory(userIdToSend, 1, 30);
        }
      } else {
        console.error('No valid response received from API');
            }
    } catch (error: any) {
      console.error('Error sending message:', error);
    } finally {
        setLoadingMessage(false);
    }
  };

  // Expose the submit function globally for external components
  React.useEffect(() => {
    (window as any).submitMessageDirectly = submitMessageDirectly;
    return () => {
      delete (window as any).submitMessageDirectly;
    };
  }, [submitMessageDirectly]);
 
  // Auto-resize textarea
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const target = e.target;
    setMessage(target.value);
    
    // Auto-resize
    target.style.height = 'auto';
    target.style.height = target.scrollHeight + 'px';
  };
 
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };
 
  // File upload configuration
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'text/*': ['.txt', '.md', '.csv', '.json'],
      'application/pdf': ['.pdf'],
      'application/msword': ['.doc'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'application/vnd.ms-excel': ['.xls'],
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
      'application/vnd.ms-powerpoint': ['.ppt'],
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx']
    },
    maxSize: 10485760, // 10MB
    multiple: true, // Allow multiple files
    onDrop: (acceptedFiles) => {
      setSelectedFiles(prev => [...prev, ...acceptedFiles]);
    }
  });
 
  // Remove a file from the selection
  const removeFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };
 
  return (
    <div className="mx-auto w-full">
      {/* File upload dropzone - only show for external mode */}
      {!isInternalMode && (
        <>
          <div
            {...getRootProps()}
            className={`mb-3 border-2 border-dashed rounded-md p-4 text-center cursor-pointer transition-colors ${isDragActive ? 'border-groww-green bg-groww-green/5' : 'border-gray-300 hover:border-groww-blue'
              }`}
          >
            <input {...getInputProps()} />
            <p className="text-sm text-gray-600">
              {isDragActive
                ? 'Drop the files here...'
                : 'Drag & drop files here, or click to select'}
            </p>
            <p className="text-xs text-gray-500 mt-1">
              Supported: PDF, TXT, DOC, DOCX, XLS, XLSX, CSV
            </p>
          </div>
 
          {/* Selected files preview */}
          {selectedFiles.length > 0 && (
            <div className="mb-3 space-y-2">
              {selectedFiles.map((file, index) => (
                <div key={index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                  <div className="flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm text-gray-700 truncate max-w-xs">{file.name}</span>
                  </div>
                  <button
                    onClick={() => removeFile(index)}
                    className="text-gray-500 hover:text-red-500"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          )}
        </>
      )}
 
      {/* Message input form */}
      <form onSubmit={handleSubmit} className="relative">
        <div className="flex items-center relative border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-groww-blue focus-within:border-transparent">
          {/* Search Toggle Button - only show for external mode */}
          {!isInternalMode && (
            <button
              type="button"
              onClick={() => {
                if (isBingSearchAvailable) {
                  setIsSearchMode(!isSearchMode);
                  setIsImageGeneration(false);
                }
              }}
              disabled={!isBingSearchAvailable}
              className={`flex flex-row gap-2 px-2 text-sm border border-gray-300 text-md absolute left-4 top-1/2 transform -translate-y-1/2 p-1 rounded-full ${isSearchMode
                  ? "text-blue-500 bg-blue-50 hover:border-blue-500"
                  : isBingSearchAvailable
                    ? "text-gray-500 hover:text-gray-600 hover:border-gray-600"
                    : "text-gray-400 cursor-not-allowed"
                }`}
              title={
                isBingSearchAvailable
                  ? isSearchMode
                    ? "Switch to normal mode"
                    : "Search the web"
                  : "You need to use gpt-4o model to use web search"
              }
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <line
                  x1="2"
                  y1="12"
                  x2="22"
                  y2="12"
                  stroke="currentColor"
                  strokeWidth="2"
                />
                <path
                  d="M12 2C15 6 15 18 12 22C9 18 9 6 12 2Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  fill="none"
                />
              </svg>
              Search
            </button>
          )}
 
          {/* Textarea with adjusted padding for button space */}
          <textarea
            ref={textareaRef}
            value={message}
            onChange={handleTextareaChange}
            onKeyDown={handleKeyDown}
            placeholder={isInternalMode ? "Ask anything (streaming mode)" : (isImageGeneration ? "Describe the image you want to generate..." : "Ask anything")}
            className={`w-full py-3 resize-none rounded-lg overflow-hidden focus:outline-none ${!isInternalMode ? 'pl-32 pr-12' : 'pl-4 pr-12'}`}
            rows={1}
            disabled={loadingMessage || isStreaming}
          />
 
          {/* Send button */}
          <button
            type="submit"
            disabled={(!message.trim() && selectedFiles.length === 0) || loadingMessage || isStreaming}
            className={`absolute right-1 top-1/2 transform -translate-y-1/2 p-1 rounded-full focus:outline-none ${(!message.trim() && selectedFiles.length === 0) || loadingMessage || isStreaming
                ? 'text-gray-400 cursor-not-allowed'
                : 'text-groww-green hover:bg-groww-green/10'
              }`}
            title={isImageGeneration ? "Generate Image" : "Send Message"}
          >
            {(loadingMessage || isStreaming) ? (
              <svg className="animate-spin h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-8.707l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13a1 1 0 102 0V9.414l1.293 1.293a1 1 0 001.414-1.414z" clipRule="evenodd" />
              </svg>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};
 
export default ChatInput;
